/**
 * Bandeau du haut, 48 px, toujours a la meme place (design, sections 3 et 6).
 * Chaque etat a son icone ET son texte : jamais la couleur seule.
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FRESHNESS } from './catalogFreshness';
import { CONNECTION } from './heartbeat';
import { palette } from './theme';

const timeFormat = new Intl.DateTimeFormat('fr-CA', { hour: '2-digit', minute: '2-digit' });

const STATES = {
    unenrolled: { bg: palette.border, fg: palette.text, Icon: InfoOutlinedIcon, text: 'Poste non activé' },
    [CONNECTION.CHECKING]: { bg: palette.infoTint, fg: palette.infoOnTint, Icon: SyncIcon, text: 'Vérification de la connexion…' },
    [CONNECTION.OFFLINE]: { bg: palette.warningTint, fg: palette.warningOnTint, Icon: CloudOffIcon, text: 'Hors ligne : les ventes sont enregistrées sur ce poste' },
    [CONNECTION.ONLINE]: { bg: palette.successTint, fg: palette.successOnTint, Icon: CheckCircleIcon, text: 'Toutes les ventes sont dans l\'ERP' },
};

function useClock(intervalMs = 30_000) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
}

/**
 * Pastille « catalogue perime » (design 3) : ajoutee au bandeau, jamais a sa
 * place. Ambre apres 24 h, rouge apres 72 h ; icone et texte, jamais la
 * couleur seule.
 */
export function CatalogPill({ freshness }) {
    if (!freshness || !freshness.label) return null;
    const expired = freshness.state === FRESHNESS.EXPIRED;
    const Icon = expired ? BlockIcon : WarningAmberIcon;
    return (
        <Box
            data-testid="pastille-catalogue"
            data-etat={freshness.state}
            sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, flex: '0 1 auto', minWidth: 0,
                px: 1.5, py: 0.5, borderRadius: 999, border: '2px solid',
                bgcolor: expired ? palette.errorTint : palette.warningTint,
                color: expired ? palette.errorOnTint : palette.warningOnTint,
                borderColor: expired ? palette.error : palette.warning,
            }}
        >
            <Icon aria-hidden="true" sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {freshness.label}
            </Typography>
        </Box>
    );
}

export default function StatusBanner({ connection, terminal, freshness = null, employeeName = null }) {
    const now = useClock();
    const state = terminal ? STATES[connection] || STATES[CONNECTION.CHECKING] : STATES.unenrolled;
    const { Icon } = state;
    return (
        <Box
            component="header"
            role="status"
            data-testid="bandeau"
            data-connexion={terminal ? connection : 'non-active'}
            sx={{
                height: 48, flex: '0 0 48px', display: 'flex', alignItems: 'center', gap: 2, px: 2,
                bgcolor: state.bg, color: state.fg, overflow: 'hidden', whiteSpace: 'nowrap',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Icon aria-hidden="true" />
                <Typography sx={{ fontSize: 18, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {state.text}
                </Typography>
            </Box>
            {terminal && <CatalogPill freshness={freshness} />}
            {terminal && (
                <Typography sx={{ fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {[terminal.store_name, terminal.name].filter(Boolean).join(' · ')}
                </Typography>
            )}
            {employeeName && (
                <Typography data-testid="bandeau-employe" sx={{ fontSize: 16, fontWeight: 700, flex: '0 0 auto' }}>{employeeName}</Typography>
            )}
            <Typography sx={{ fontSize: 18, fontWeight: 700, flex: '0 0 auto' }}>{timeFormat.format(now)}</Typography>
        </Box>
    );
}

/**
 * Stockage non protege (design, section 3) : pastille rouge, icone et texte.
 */
export function StorageWarning() {
    return (
        <Box
            role="alert"
            data-testid="avertissement-stockage"
            sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                bgcolor: palette.errorTint, color: palette.errorOnTint,
            }}
        >
            <WarningAmberIcon aria-hidden="true" />
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                Ce poste peut perdre ses ventes si Chrome est vidé. Avertissez le responsable.
            </Typography>
        </Box>
    );
}
